import React, { ChangeEvent, UIEvent, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { InputMui } from '../../input-mui/input-mui';
import { Dialog } from '../base';
import './confirmation.scss';

type DialogConfirmationProps = {
  show: boolean;
  setShow: (show: boolean) => void;
  header?: string;
  headerValue?: string;
  content?: string;
  primaryBtnText?: string;
  secondaryBtnText?: string;
  tertiaryBtnText?: string;
  primaryBtnAction?: (event?: UIEvent) => void;
  secondaryBtnAction?: (event?: UIEvent) => void;
  tertiaryBtnAction?: (event?: UIEvent) => void;
  direction?: 'row' | 'column';
  primaryBtnDisabled?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  headerIcon?: React.ReactNode;
  contentClassName?: string;
  timer?: boolean;
  timerCancel?: boolean;
  confirmInput?: { label: string; value: string };
  backgroundBlur?: boolean;
};

export const DialogConfirmation: React.FC<DialogConfirmationProps> = ({
  header,
  headerValue,
  content,
  primaryBtnText,
  secondaryBtnText,
  tertiaryBtnText,
  primaryBtnAction,
  secondaryBtnAction,
  tertiaryBtnAction,
  show,
  setShow,
  direction = 'row',
  children,
  icon,
  headerIcon,
  primaryBtnDisabled,
  contentClassName,
  timer,
  timerCancel,
  confirmInput,
  backgroundBlur,
}) => {
  const interval = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalCancel = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [time, setTime] = useState<number>(0);
  const [timeCancel, setTimeCancel] = useState<number>(0);
  const [confirm, setConfirm] = useState<string>('');

  const removeInterval = () => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }
  };

  const removeIntervalCancel = () => {
    if (intervalCancel.current) {
      clearInterval(intervalCancel.current);
      intervalCancel.current = null;
    }
  };

  useEffect(() => {
    const letCount = () =>
      setTime((prev) => {
        if (prev === 0) {
          removeInterval();
          return prev;
        }

        return prev - 1;
      });

    if (show && timer) {
      setTime(9);
      interval.current = setInterval(letCount, 1000);
    } else {
      removeInterval();
    }

    return () => {
      removeInterval();
    };
  }, [timer, show]);

  useEffect(() => {
    const letCount = () =>
      setTimeCancel((prev) => {
        if (prev === 0) {
          removeIntervalCancel();
          return prev;
        }

        return prev - 1;
      });

    if (show && timerCancel) {
      setTimeCancel(9);
      intervalCancel.current = setInterval(letCount, 1000);
    } else {
      removeIntervalCancel();
    }

    return () => {
      removeIntervalCancel();
    };
  }, [timerCancel, show]);

  const primaryButtonDisabled = useMemo(() => {
    if (!confirmInput) {
      return primaryBtnDisabled || !!time;
    } else {
      return primaryBtnDisabled || !!time || confirmInput.value !== confirm;
    }
  }, [primaryBtnDisabled, confirmInput, time, confirm]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => setConfirm(event.target.value);

  const btnPrimaryVisible = !!primaryBtnText && !!primaryBtnAction;
  const btnSecondaryVisible = !!secondaryBtnText && !!secondaryBtnAction;
  const btnTertiaryVisible = !!tertiaryBtnText && !!tertiaryBtnAction;

  const renderDivider = () => <div className={`dialog-confirmation__button-divider ${direction}`} />;

  const renderButton = (
    type: 'primary' | 'secondary',
    btnText: string,
    handleAction: (event?: UIEvent) => void,
    time: number,
    disabled?: boolean,
  ) => {
    return (
      <button
        className={`dialog-confirmation__button dialog-confirmation__button--${type}`}
        disabled={disabled}
        onClick={handleAction}
      >
        {`${btnText}${time ? ` (${time}s)` : ''}`}
      </button>
    );
  };

  return (
    <Dialog show={show} setShow={setShow} backdropCloseDisabled backgroundBlur={backgroundBlur}>
      <div className={`dialog-confirmation dialog-confirmation--direction-${direction}`}>
        {icon && icon}
        {headerValue ? (
          <div className="dialog-confirmation__header dialog-confirmation__header--container">
            <span>{header}:</span>
            <span>{headerValue}</span>
          </div>
        ) : (
          <h4
            className={classNames('dialog-confirmation__header', {
              'with-icon': !!headerIcon,
              'm-b-0': !primaryBtnText && !secondaryBtnText,
            })}
          >
            {header}
            {headerIcon ? headerIcon : null}
          </h4>
        )}
        <div
          className={classNames('dialog-confirmation__content', {
            [contentClassName as string]: contentClassName,
          })}
        >
          {content || children}
        </div>
        {confirmInput && (
          <div className="m-t-24">
            <InputMui value={confirm} placeholder={confirmInput.label} onChange={handleInputChange} reverse />
          </div>
        )}
        {btnPrimaryVisible || btnSecondaryVisible || btnTertiaryVisible ? (
          <div
            className={classNames('dialog-confirmation__buttons-wrapper', {
              wrap: direction === 'row' && btnPrimaryVisible && btnSecondaryVisible && btnTertiaryVisible,
            })}
          >
            {btnTertiaryVisible && (
              <>
                {renderButton('secondary', tertiaryBtnText, tertiaryBtnAction, 0)}
                {renderDivider()}
              </>
            )}
            {btnSecondaryVisible && (
              <>
                {renderButton('secondary', secondaryBtnText, secondaryBtnAction, timeCancel, !!timeCancel)}
                {renderDivider()}
              </>
            )}
            {btnPrimaryVisible && (
              <>{renderButton('primary', primaryBtnText, primaryBtnAction, time, primaryButtonDisabled)}</>
            )}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
};
